import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme
} from '@mui/material';
import { LockKeyhole, Save } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { apiClient } from 'utils/ApiClient';
import { API_BASE_URL } from 'envConfig';

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  repeatPassword: string;
}

export const Security: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.authentication.user);
  const [formData, setFormData] = useState<ChangePasswordData>({
    currentPassword: '',
    newPassword: '',
    repeatPassword: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleInputChange = (field: keyof ChangePasswordData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!formData.repeatPassword.trim()) {
      newErrors.repeatPassword = 'Please repeat your new password';
    } else if (formData.newPassword !== formData.repeatPassword) {
      newErrors.repeatPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.post('/user/changepassword', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      // If we reach here, the request was successful
      setSnackbar({
        open: true,
        message: 'Password changed successfully!',
        severity: 'success'
      });
      
      // Reset form
      setFormData({
        currentPassword: '',
        newPassword: '',
        repeatPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'An error occurred while changing password',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Security Settings
      </Typography>
      <Card sx={{ maxWidth: 800, margin: 'auto', p: 3, borderRadius: '12px', boxShadow: 3 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <LockKeyhole
              size={48}
              color={theme.palette.settings?.securityHeroIconColor ?? theme.palette.secondary.main}
            />
            <Typography variant="h5" mt={2}>
              Change Password
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Update your account password to keep it secure
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Current Password"
              type="password"
              value={formData.currentPassword}
              onChange={(e) => handleInputChange('currentPassword', e.target.value)}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword}
              fullWidth
              required
              variant="outlined"
              size="small"
            />

            <TextField
              label="New Password"
              type="password"
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              fullWidth
              required
              variant="outlined"
              size="small"
            />

            <TextField
              label="Repeat New Password"
              type="password"
              value={formData.repeatPassword}
              onChange={(e) => handleInputChange('repeatPassword', e.target.value)}
              error={!!errors.repeatPassword}
              helperText={errors.repeatPassword}
              fullWidth
              required
              variant="outlined"
              size="small"
            />

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              sx={{ mt: 3 }}
            >
              {loading ? 'Changing Password...' : 'CHANGE PASSWORD'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 